import {observer, useLocalObservable} from 'mobx-react'
import React from 'react'
import {useController} from 'react-hook-form'
import styled, {css} from 'styled-components'

import {useFormControl} from '@/src/shared/modules/form'
import {ViewLabel} from '@/src/shared/ui/view-label'

import {Label} from '@/src/shared/ui/label'

import {FormInput, InputProps, InputUI} from './input'
import {RequiredInputMarkIfNeed} from './input/input'

export const ReadableInput = observer(
  ({
    children,
    labelTransform,
    showPlaceholder = false,
    ...props
  }: {input?: React.FC<any>; labelTransform?: (value: any) => string | undefined; showPlaceholder?: boolean} & InputProps &
    React.PropsWithChildren) => {
    const formControl = useFormControl()
    const {field, fieldState} = useController({name: props.name, control: formControl.control})

    const formInputVisible = useLocalObservable(() => ({
      visible: false,

      showInput() {
        this.visible = true
      },
    }))

    const placeholderLabel = showPlaceholder ? `${props.placeholder}: ` : ''

    if (!formInputVisible.visible) {
      return (
        <Container className={props.className} label={children} required={props.required}>
          <UI.ViewLabel as={ViewLabel} onClick={formInputVisible.showInput} $invalid={fieldState.invalid}>
            {labelTransform && field.value
              ? `${placeholderLabel}${labelTransform(field.value)}`
              : field.value
                ? `${placeholderLabel}${field.value}`
                : children
                  ? `${placeholderLabel}${children}`
                  : props.placeholder}
          </UI.ViewLabel>
        </Container>
      )
    }

    const InputComponent = props.input ?? FormInput

    return (
      <Container className={props.className} label={children} required={props.required}>
        <InputComponent {...props} prefix={(placeholderLabel && placeholderLabel) || undefined} />
      </Container>
    )
  },
)

export const UI = {
  ViewLabel: styled.div<{$invalid: boolean}>`
    min-height: 45px;
    display: flex;
    align-items: center;
    text-wrap: nowrap;

    ${props =>
      props.$invalid &&
      css`
        color: ${props.theme.colors.error};
      `}
  `,
  Input: styled(FormInput),
}

const Container = ({
  children,
  className,
  label,
  required,
}: {className?: string; label?: React.ReactNode; required?: boolean} & React.PropsWithChildren) => {
  return (
    <InputUI.Wrapper className={className}>
      {label && (
        <RequiredInputMarkIfNeed required={required}>
          <Label>{label}</Label>
        </RequiredInputMarkIfNeed>
      )}
      {children}
    </InputUI.Wrapper>
  )
}
